import { Disclosure, Transition } from "@headlessui/react";
import {
  MapPinIcon,
  BriefcaseIcon,
  UsersIcon,
  CubeIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { Heart, ThumbsUp, ThumbsDown, Copy, ChevronDown } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";

import { useAlert } from "../../../../constants/AlertProvider";
import { slugify } from "../../../../utils/slugify";
import { formatField } from "../../../../utils/universalFormatter";

export function JobCard({ job, onJobUpdated }) {
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const showAlert = useAlert();

  // Split the keywords string into an array, handling potential empty strings
  const keywordTags = job.keywords
    ? job.keywords
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean)
    : [];

  const handleShare = (job) => {
    const slug = slugify(job.title, job.companyData.name, job.id);
    const url = `${window.location.origin}/job/${slug}/${job.id}`;

    navigator.clipboard.writeText(url);
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const applyAction = async (patch) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const res = await axios.post(
        "/api/match-results/save-job/",
        { job: job.id, ...patch },
        { withCredentials: true, headers: { "X-CSRFToken": Cookies.get("csrftoken") } },
      );
      onJobUpdated?.(job.id, {
        matchId: res.data.id,
        isSaved: res.data.is_saved,
        isGoodFit: res.data.is_good_fit,
        isNotForMe: res.data.is_not_for_me,
      });
    } catch (error) {
      showAlert("Failed to update job status", "error");
      console.error("Failed to update match", error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleSaved = () => applyAction({ is_saved: !job.isSaved });
  const toggleNotForMe = () =>
    applyAction({ is_not_for_me: !job.isNotForMe, is_good_fit: job.isNotForMe ? job.isGoodFit : false });
  const toggleGoodFit = () =>
    applyAction({ is_good_fit: !job.isGoodFit, is_not_for_me: job.isGoodFit ? job.isNotForMe : false });

  return (
    <div className="w-full">
      <Disclosure
        as="div"
        className="border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow duration-300"
      >
        {({ open }) => (
          <>
            <Disclosure.Button className="w-full p-4 text-left">
              <div className="flex flex-row gap-x-4 items-start">
                {/* Company Logo or Fallback */}
                {job.companyData.logo ? (
                  <img
                    alt={`${job.companyData.name} logo`}
                    className="w-10 h-10 object-contain"
                    src={job.companyData.logo}
                  />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-md">
                    <BuildingOfficeIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                )}

                {/* Job Info */}
                <div className="flex-grow flex flex-col gap-y-2">
                  {/* Job Title */}
                  <h1 className="text-gray-900 dark:text-gray-100 font-bold text-[16px]">
                    {job.title},{" "}
                    <span className="font-semibold">
                      {job.companyData.name}
                    </span>
                  </h1>

                  {/* Meta Info */}
                  <div className="w-4/5 flex flex-wrap gap-x-6 gap-y-2 text-gray-600 dark:text-gray-400 text-[13px]">
                    <div className="flex items-center gap-x-1">
                      <BriefcaseIcon className="w-4 h-4" />
                      <span>{formatField("job_type", job.job_type)}</span>
                    </div>
                    <div className="flex items-center gap-x-1">
                      <CubeIcon className="w-4 h-4" />
                      <span className="text-sm">{job.keywords}</span>
                    </div>
                    <div className="flex items-center gap-x-1">
                      <CurrencyDollarIcon className="w-4 h-4" />
                      <span>
                        {" "}
                        {formatField("salary", job.salary_min)}K -{" "}
                        {formatField("salary", job.salary_max)}K
                      </span>
                    </div>
                    <div className="flex items-center gap-x-1">
                      <MapPinIcon className="w-4 h-4" />
                      <span>
                        {job.city}, {job.state}
                      </span>
                    </div>
                    <div className="flex items-center gap-x-1">
                      <BriefcaseIcon className="w-4 h-4" />
                      <span>{job.years_of_experience} Years of Experience</span>
                    </div>
                    <div className="flex items-center gap-x-1">
                      <UsersIcon className="w-4 h-4" />
                      <span>
                        {formatField(
                          "employee_count",
                          job.companyData.employee_count,
                        )}{" "}
                        Employees
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronDown
                  className={`${open ? "rotate-180" : ""} h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300`}
                />
              </div>
            </Disclosure.Button>

            <Transition
              enter="transition duration-300 ease-out"
              enterFrom="transform -translate-y-4 opacity-0"
              enterTo="transform translate-y-0 opacity-100"
              leave="transition duration-200 ease-out"
              leaveFrom="transform translate-y-0 opacity-100"
              leaveTo="transform -translate-y-4 opacity-0"
            >
              <Disclosure.Panel className="px-4 pb-4 flex flex-col gap-y-4">
                {/* Description */}
                <p className="text-[13px] font-normal text-gray-500 dark:text-gray-400 pt-4 border-t border-gray-100 dark:border-gray-700">
                  {job.description}
                </p>

                <div className="flex flex-row gap-x-4 mb-4">
                  {keywordTags.map((tag, index) => (
                    <div
                      key={index}
                      className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 text-[12px] rounded-md"
                    >
                      {tag}
                    </div>
                  ))}
                </div>

                <div className="w-full border border-gray-100 dark:border-gray-700 rounded-lg grid grid-cols-4">
                  {/* Save Button */}
                  <button
                    className="group p-4 border-r border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center cursor-pointer flex-row gap-x-2 transition rounded-l-lg disabled:opacity-50"
                    onClick={toggleSaved}
                    disabled={isSaving}
                  >
                    <span>
                      <Heart
                        className={`transition-colors ${job.isSaved ? "fill-[#E11D48] text-[#E11D48]" : "text-gray-500 dark:text-gray-400 group-hover:fill-[#E11D48] group-hover:text-[#E11D48]"}`}
                        size={14}
                      />
                    </span>
                    <span className="text-[14px] font-semibold transition-colors text-gray-900 dark:text-gray-100">
                      {job.isSaved ? "Saved!" : "Save"}
                    </span>
                  </button>

                  {/* Not for Me */}
                  <button
                    className="group p-4 border-r border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center cursor-pointer flex-row gap-x-2 transition disabled:opacity-50"
                    onClick={toggleNotForMe}
                    disabled={isSaving}
                  >
                    <span>
                      <ThumbsDown
                        className={`transition-colors ${job.isNotForMe ? "fill-blue-500 text-blue-500" : "text-gray-500 dark:text-gray-400 group-hover:text-blue-500 group-hover:fill-blue-500"}`}
                        size={14}
                      />
                    </span>
                    <span className="text-[14px] font-semibold transition-colors text-gray-900 dark:text-gray-100">
                      Not for Me
                    </span>
                  </button>

                  {/* Good fit */}
                  <button
                    className="group p-4 border-r border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center cursor-pointer flex-row gap-x-2 transition disabled:opacity-50"
                    onClick={toggleGoodFit}
                    disabled={isSaving}
                  >
                    <span>
                      <ThumbsUp
                        className={`transition-colors ${job.isGoodFit ? "fill-blue-500 text-blue-500" : "text-gray-500 dark:text-gray-400 group-hover:text-blue-500 group-hover:fill-blue-500"}`}
                        size={14}
                      />
                    </span>
                    <span className="text-[14px] font-semibold transition-colors text-gray-900 dark:text-gray-100">
                      Good fit
                    </span>
                  </button>

                  {/* share */}
                  <button
                    className="group p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center cursor-pointer flex-row gap-x-2 transition rounded-r-lg"
                    onClick={() => handleShare(job)}
                  >
                    <span>
                      <Copy
                        className={`transition-colors ${copied ? "fill-blue-500 text-blue-500" : "text-gray-500 dark:text-gray-400 group-hover:text-blue-500 group-hover:fill-blue-500"}`}
                        size={14}
                      />
                    </span>
                    <span className="text-[14px] font-semibold transition-colors text-gray-900 dark:text-gray-100">
                      {copied ? "Link Copied!" : "Share"}
                    </span>
                  </button>
                </div>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </div>
  );
}
